/**
 * Softphone diagnostics: the one module of it the rest of the app may import.
 *
 * What it is. A recorder that keeps a ring buffer of what the softphone and the page did - page
 * lifecycle, the Telnyx socket and calls, heartbeats, ringtone and remote-audio plays, microphone
 * tracks, a stats summary during a call - and a bottom sheet, opened with `?softphone-diagnostics`,
 * that shows it on the phone. It exists to measure real phones (docs/softphone-mobile-plan.md, P0.2),
 * where nobody can open a console. It is a testing tool, not a product feature.
 *
 * Why production has none of it. `VUE_APP_SOFTPHONE_DIAGNOSTICS` is a build-time value, `false` in
 * `.env.production`. Every dynamic `import()` of the recorder or the panel sits inside a comparison
 * of that value, written out literally. webpack replaces it with the string, folds the comparison and
 * never parses the dead branch, so no dependency is created and no chunk is emitted. A variable, a
 * helper or a value read at run time would defeat that, which is why the comparison is repeated below
 * instead of shared. This file is the only part that is always bundled, and with the switch off every
 * function in it does nothing.
 *
 * What it must never do. Throw into softphone code, or be awaited by it. Every function here
 * swallows its own failures, and callers fire and forget.
 */

// Namespaced so it can't collide with app code. It is not `SOFTPHONE_DEV_HOOK_KEY`: that hook is gated
// on NODE_ENV, so a staging build (a production-mode build) does not have it, and a script driving a
// phone against staging still needs to read what the page recorded.
export const SOFTPHONE_DIAGNOSTICS_WINDOW_KEY = '__ringbaxSoftphoneDiagnostics__'

// The recorder saves each tab's buffer in localStorage under this prefix plus the tab's id. It lives here
// because the facade reads the saved buffers as the page opens (see `readStartupSnapshot`).
export const SOFTPHONE_DIAGNOSTICS_BUFFER_PREFIX = 'ringbax.softphoneDiagnostics.buffer.'

// What is held for the recorder while its chunk loads. It loads at start-up, long before anyone can go
// live, so this only ever holds the first few events; the oldest go first if it somehow fills.
const MAX_EARLY_EVENTS = 100

let startPromise = null
let recorder = null
let earlyEvents = []
let pendingClient = null
let hasWarnedAboutLoad = false

/**
 * Read at call time, not once at load, so a spec can switch it. In a build it is a constant.
 *
 * The functions below repeat the comparison instead of calling this one. A comparison written out is
 * folded by webpack, which leaves the rest of the function unreachable and the minifier removes it, so
 * in production each of them is an empty function. Behind a call to this one the body would stay.
 */
export function isSoftphoneDiagnosticsEnabled() {
  return process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS === 'true'
}

/**
 * Records one event. `data` is an object, or a function returning one so that building it costs
 * nothing when diagnostics are off. Before the recorder has loaded the event is kept with the time it
 * happened, because that time is what places it on the page's timeline; the recorder takes them over
 * when it starts.
 */
export function recordSoftphoneDiagnostic(category, name, data) {
  if (process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS !== 'true') {
    return
  }

  try {
    const resolved = typeof data === 'function' ? data() : data

    if (recorder) {
      recorder.record(category, name, resolved)

      return
    }

    earlyEvents.push({ cat: category, name, data: resolved, perf: performance.now(), wall: Date.now() })

    if (earlyEvents.length > MAX_EARLY_EVENTS) {
      earlyEvents.shift()
    }
  } catch (error) {
    // Diagnostics never change what the softphone does.
  }
}

/**
 * What the browser says about user activation and visibility right now. Read synchronously BEFORE
 * `play()`: activation can expire before the promise settles, and a refusal is only explained by what
 * was true when the call was made. Null when diagnostics are off.
 */
export function readActivationContext() {
  if (process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS !== 'true') {
    return null
  }

  try {
    const activation = navigator.userActivation

    return {
      userActivation: activation
        ? { isActive: Boolean(activation.isActive), hasBeenActive: Boolean(activation.hasBeenActive) }
        : null,
      visibility: document.visibilityState
    }
  } catch (error) {
    return null
  }
}

/**
 * What the page looked like as it opened, taken before the app has mounted: the address, and every
 * buffer the recorder saved in localStorage.
 *
 * Both are gone by the time the recorder's chunk has loaded. The login page signs the user out, signing
 * out calls `localStorage.clear()`, and the router drops the query from the address on its way to the
 * login page. That is the very case the saved buffers are for - a reload, or a Home Screen relaunch,
 * after the session has expired - so they are read here, synchronously, and the recorder puts them back.
 */
function readStartupSnapshot() {
  const snapshot = { search: '', buffers: {} }

  try {
    snapshot.search = window.location.search
  } catch (error) {
    // No address to read.
  }

  try {
    const storage = window.localStorage

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)

      if (key && key.startsWith(SOFTPHONE_DIAGNOSTICS_BUFFER_PREFIX)) {
        snapshot.buffers[key] = storage.getItem(key)
      }
    }
  } catch (error) {
    // Storage is blocked: there is nothing saved to keep.
  }

  return snapshot
}

/**
 * Loads the recorder and starts it. One shared promise, so every caller gets the same recorder and
 * it is only ever started once. Resolves to the recorder, or to null when diagnostics are off or the
 * chunk could not be loaded - nothing here ever rejects.
 */
export function startSoftphoneDiagnostics({ store } = {}) {
  if (startPromise) {
    return startPromise
  }

  // Spelled out instead of calling `isSoftphoneDiagnosticsEnabled()`: webpack folds the comparison
  // only when it can see the variable here, and only a folded comparison drops the `import()`. The
  // `import()` has to be inside this `if`, written like this; it cannot move into a helper.
  if (process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS === 'true') {
    const startup = readStartupSnapshot()

    startPromise = import(/* webpackChunkName: "softphone-diagnostics" */ './recorder')
      .then(({ createSoftphoneDiagnosticsRecorder }) => {
        recorder = createSoftphoneDiagnosticsRecorder({ store, early: earlyEvents, pendingClient, startup })
        earlyEvents = []
        pendingClient = null

        return recorder
      })
      .catch(error => {
        if (!hasWarnedAboutLoad) {
          hasWarnedAboutLoad = true

          console.warn('Softphone diagnostics could not start', error)
        }

        return null
      })

    return startPromise
  }

  return Promise.resolve(null)
}

export function getSoftphoneDiagnosticsRecorder() {
  return recorder
}

/**
 * Hands the recorder a Telnyx client to listen to. The client is built when the agent goes live, which
 * can only be after start-up, but the chunk may still be loading, so the latest one is kept until the
 * recorder is there to take it.
 */
export function observeTelnyxClient(client) {
  if (process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS !== 'true') {
    return
  }

  try {
    if (recorder) {
      recorder.observeTelnyxClient(client)
    } else {
      pendingClient = client
    }
  } catch (error) {
    // Diagnostics never change what the softphone does.
  }
}

/**
 * Lets go of a Telnyx client once its transport has disconnected: its handlers come off the SDK, and
 * the panel stops offering it for the microphone check. A client let go before the recorder has loaded
 * is simply not handed over.
 */
export function releaseTelnyxClient(client) {
  if (process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS !== 'true') {
    return
  }

  try {
    if (recorder) {
      recorder.releaseTelnyxClient(client)
    } else if (pendingClient === client) {
      pendingClient = null
    }
  } catch (error) {
    // Diagnostics never change what the softphone does.
  }
}

// The panel, as an async component for the dashboard layout to register. Null outside builds that
// have diagnostics; the layout renders nothing then, and the page never fetches the chunk.
export const softphoneDiagnosticsPanel = process.env.VUE_APP_SOFTPHONE_DIAGNOSTICS === 'true'
  ? () => import(/* webpackChunkName: "softphone-diagnostics" */ '@/components/softphone/SoftphoneDiagnostics.vue')
  : null
